import React, { FC, Suspense, useEffect, useRef, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation, useRefetchableFragment } from 'react-relay/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchQuery } from 'relay-runtime';
import MaterialIcon from 'src/components/widgets/leftAppSidebar/MaterialIcon';
import { backofficeUrl, T, toGlobalID, toLocalID } from 'src/shared/helpers';
import CogIcon from 'src/shared/icons/cog-solid.svg';
import VerticalThreePoint from 'src/shared/icons/ellipsis-v-solid.svg';
import LinkIcon from 'src/shared/icons/external-link-alt-solid.svg';
import styled from 'styled-components';
import { BasicButton, ButtonActions, Link, PrimaryButton, SelectActions, TextButton } from '../../widgets/buttons';
import { DialogWindow, MessageDialog, QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { EmptyList } from '../../widgets/emptyList';
import { SectionTitle } from '../../widgets/headers';
import { HelpIcon, HelpIconContainer } from '../../widgets/helpIconContainer';
import { Icon } from '../../widgets/icon';
import { ContainerBase } from '../../widgets/layouts';
import { removeNotification, showErrorNotification, showNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import { PageHeader } from '../../widgets/pageHeader';
import { Spinner } from '../../widgets/spinner';
import { Row, Table, TableHeading } from '../../widgets/table';
import { ZTooltip } from '../../widgets/ztooltip';
import SalesChannelIcon from '../products/salesChannelIcon';
import { salesChannelConfirmShopifyChargeMutation } from './__generated__/salesChannelConfirmShopifyChargeMutation.graphql';
import { salesChannelCreatePaymentConfirmedTaskMutation } from './__generated__/salesChannelCreatePaymentConfirmedTaskMutation.graphql';
import { salesChannelGenerateEditSubscriptionTaskMutation } from './__generated__/salesChannelGenerateEditSubscriptionTaskMutation.graphql';
import { salesChannelGenerateProductSwitcherTaskMutation } from './__generated__/salesChannelGenerateProductSwitcherTaskMutation.graphql';
import { salesChannelPollUnsubscribeTaskQuery } from './__generated__/salesChannelPollUnsubscribeTaskQuery.graphql';
import { salesChannelProductsOnDefaultQuery } from './__generated__/salesChannelProductsOnDefaultQuery.graphql';
import { salesChannelRemoveIntegrationMutation } from './__generated__/salesChannelRemoveIntegrationMutation.graphql';
import { salesChannelSetStatusMutation } from './__generated__/salesChannelSetStatusMutation.graphql';
import { ECommerce, salesChannelsActive$data, salesChannelsActive$key } from './__generated__/salesChannelsActive.graphql';
import { salesChannelsQuery } from './__generated__/salesChannelsQuery.graphql';

import { useRelayEnvironment } from 'react-relay';
import Loader from 'src/components/widgets/loader';
import { useAppState } from 'src/shared/appState';
import config from 'src/shared/config';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { Center } from '../../../shared/globals';
import { UnclickableTextButton } from '../products/productSalesChannelList';
import { salesChannelCheckSellerPlanQuery } from './__generated__/salesChannelCheckSellerPlanQuery.graphql';
import AddSalesChannelDialog from './addSalesChannelDialog';
import ApiIntegrationSalesChannelDialog from './apiIntegrationSalesChannelDialog';
import ApiKeyDialog from './apiKeyDialog';
import CCloudIntegrationSalesChannelDialog from './cCloudIntegrationSalesChannelDialog';
import { generateStoreNumbers } from './helpers';
import PaymentSuccessDialog from './paymentSuccessDialog';
import RenameSalesChannelDialog from './renameSalesChannelDialog';
import StoreUrlSalesChannelDialog from './storeUrlSalesChannelDialog';
import SwitchSalesChannelDialog, { SellerPlanCheckInfo, TASKSTATUS } from './switchSalesChannelDialog';

const StyledImg = styled.div`
    > div {
        width: 32px;
        height: 32px;
    }

    img {
        width: 30px;
        height: 30px;
    }
`;

const ActionsColContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    width: 100%;
`;

const StyledCard = styled.div`
    background-color: transparent;
    margin-top: 0;
    margin-bottom: 8px;
    padding: 0;
    color: #000;
`;

const StyledLinkContainer = styled.div`
    word-wrap: nowrap;
`;

const StyledDivElementTable = styled.div`
    align-content: center;
    max-width: 100%;
`;

const StyledLink = styled(Link)`
    font-weight: 400;
    white-space: nowrap;

    span {
        max-width: 90%;
        display: inline-block;
        overflow: clip;
        text-overflow: ellipsis;
    }

    svg {
        stroke-width: 0.2px;
        margin-bottom: 3px;
        margin-left: 5px;
        vertical-align: middle;
        display: inline-block;
    }
`;

const StyledCardTitle = styled(SectionTitle)`
    margin-top: 24px;
    margin-bottom: 16px;
    text-align: left;
    font-size: 14px;
    font-weight: 600;
`;

const NameCell = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
`;

const UnclickableDisconnectButtonContainer = styled.div`
    display: flex;
    align-items: center;
`;

export const AddSalesChannelDialogWindow = styled(DialogWindow)`
    flex-basis: 100%;
    max-width: 1275px;
    padding: 56px;
`;

export const SalesChannelNotAvailableDialogWindow = styled(DialogWindow)`
    p {
        font-size: 14px;
    }

    max-width: 600px;
`;

const StyledContainerBase = styled(ContainerBase)<{ showBackButton: boolean }>`
    padding-top: ${(props) => (props?.showBackButton ? '0' : '60px')};
`;

const ActionsGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const ActionsDivider = styled.div`
    width: 1px;
    height: 20px;
    background-color: #d2d5d8;
`;

export const activeSalesChannelFragment = graphql`
    fragment salesChannelsActive on Query @refetchable(queryName: "salesChannelActiveFragmentQuery") {
        user {
            userID
            email
        }
        salesChannels(status: ACTIVE) {
            id
            detail {
                id
                properties {
                    name
                }
            }
            ecommerceSettings {
                id
                ecommerceGlobalID
                ecommerceName
                remoteUrl
                isMarketplace
                definitiveUrl
            }
        }
        productSwitcherTasks {
            taskID
            state
            taskType
            lastError
            elapsedTime
            modifiedOn
            extraData {
                taskID
                masterSalesChannelID
                secondarySalesChannelID
                delayMinutes
            }
        }
        isSellerMigrating
    }
`;

const DisabledSalesChannelContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    cursor: not-allowed;
`;

const DefaultSalesChannelProductsList: FC = () => {
    const data = useLazyLoadQuery<salesChannelProductsOnDefaultQuery>(
        graphql`
            query salesChannelProductsOnDefaultQuery {
                productsOnDefaultSalesChannel {
                    productID
                    name
                }
            }
        `,
        {},
        { fetchPolicy: 'network-only' }
    );

    if (data.productsOnDefaultSalesChannel.length === 0) return null;

    return (
        <ul>
            {data.productsOnDefaultSalesChannel.map((product) => (
                <li key={product.productID}>{product.name}</li>
            ))}
        </ul>
    );
};

const UNSUBSCRIBE_TASK_STORAGE_KEY = 'unsubscribingPlanTask';

const SalesChannels: FC<{}> = () => {
    const navigate = useNavigate();
    const [fetchKey, setFetchKey] = useState(0);
    const [checkingSellerPlanForChannelID, setCheckingSellerPlanForChannelID] = useState<string | null>(null);
    const { showDialog, closeDialog } = useDialogManager();
    const [isPending, startTransition] = useTransition();
    const { state } = useAppState();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const added = searchParams.get('added') || 'false';
    const isMaster = searchParams.get('isMaster') || 'false';

    // Payment return parameters
    const paymentSuccess = searchParams.get('paymentSuccess');
    const salesChannelIDParam = searchParams.get('salesChannelID');
    const masterSalesChannelIDParam = searchParams.get('masterSalesChannelID');
    const chargeId = searchParams.get('charge_id');

    const query = useLazyLoadQuery<salesChannelsQuery>(
        graphql`
            query salesChannelsQuery {
                shopifyAuthEcommerceSettings {
                    isTestStore
                }
                ...salesChannelsActive
                subscription {
                    areSalesChannelsEnabled
                }
            }
        `,
        {},
        { fetchKey, fetchPolicy: 'network-only' }
    );

    const [setSalesChannelStatus, isSetSalesChannelStatusPending] = useMutation<salesChannelSetStatusMutation>(graphql`
        mutation salesChannelSetStatusMutation($id: ID!, $status: SalesChannelStatus!) {
            setSalesChannelStatus(id: $id, status: $status) {
                id
            }
        }
    `);

    const [removeMasterIntegration] = useMutation<salesChannelRemoveIntegrationMutation>(graphql`
        mutation salesChannelRemoveIntegrationMutation($salesChannelID: ID) {
            removeEcommerceSettings(salesChannelID: $salesChannelID)
        }
    `);

    const [confirmShopifyCharge] = useMutation<salesChannelConfirmShopifyChargeMutation>(graphql`
        mutation salesChannelConfirmShopifyChargeMutation($input: ShopifyChargeConfirmationInput!) {
            confirmShopifyCharge(input: $input)
        }
    `);

    const [generateUnsubscribeTask] = useMutation<salesChannelGenerateEditSubscriptionTaskMutation>(graphql`
        mutation salesChannelGenerateEditSubscriptionTaskMutation($input: EditSubscriptionTaskInput!) {
            generateEditSubscriptionTask(input: $input) {
                taskID
            }
        }
    `);

    const [generateProductSwitcherTask] = useMutation<salesChannelGenerateProductSwitcherTaskMutation>(graphql`
        mutation salesChannelGenerateProductSwitcherTaskMutation($input: ProductSwitcherTaskInput!) {
            generateProductSwitcherTask(input: $input) {
                taskID
            }
        }
    `);

    const [createPaymentConfirmedTask] = useMutation<salesChannelCreatePaymentConfirmedTaskMutation>(graphql`
        mutation salesChannelCreatePaymentConfirmedTaskMutation($input: PaymentConfirmedTaskInput!) {
            generatePaymentConfirmedTask(input: $input) {
                taskID
            }
        }
    `);

    const pollUnsubscribeTaskQuery = graphql`
        query salesChannelPollUnsubscribeTaskQuery($id: ID!) {
            productSwitcherTask(id: $id) {
                id
                taskID
                state
                lastError
            }
        }
    `;

    const checkSellerPlanQuery = graphql`
        query salesChannelCheckSellerPlanQuery {
            checkSellerPlan {
                isLegacyPlan
                isV1Plan
                isProductLimitReached
                maxProductsGrow
                maxProductsScale
                currentProductCount
                canSubscribeToNewPlan
            }
            modelsCount(environment: 2)
            hasPodProducts
        }
    `;

    const Environment = useRelayEnvironment();
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [activeSalesChannels, refetch] = useRefetchableFragment<salesChannelsQuery, salesChannelsActive$key>(activeSalesChannelFragment, query);

    const isSwitchSalesChannelAvailable = useFeatureFlagWithConditions('switchSalesChannel', {
        userId: activeSalesChannels.user.userID.toString()
    });

    const masterSalesChannel = activeSalesChannels.salesChannels.filter(
        (channel) => toLocalID(channel.id) == 1 && channel.ecommerceSettings?.ecommerceName !== 'NONE'
    );

    const otherSalesChannel = activeSalesChannels.salesChannels.filter(
        (channel) => toLocalID(channel.id) != 1 && channel.ecommerceSettings?.ecommerceName !== 'NONE'
    );

    const productSwitcherTasks = activeSalesChannels.productSwitcherTasks || [];
    const isSellerMigrating = activeSalesChannels.isSellerMigrating ?? false;

    const canShowSwitchButton =
        isSwitchSalesChannelAvailable &&
        (productSwitcherTasks.length === 0 ||
            (productSwitcherTasks.length > 0 &&
                productSwitcherTasks.every(
                    (task) => task.state !== TASKSTATUS.Waiting && task.state !== TASKSTATUS.Processing && task.state !== TASKSTATUS.Error
                )));

    const headings = [
        ['#', '0.73fr'],
        [T._('Store Name', 'Admin'), '1.21fr'],
        [T._('Url', 'Admin'), 'minmax(auto, 500px)'],
        [T._('Platform', 'Admin'), '0.50fr'],
        ['', '1.14fr']
    ] as TableHeading[];

    const buildRowCells = (salesChannel: salesChannelsActive$data['salesChannels'][number]) => {
        const name: string = salesChannel.detail?.properties?.name || '';

        const storeUrl = salesChannel.ecommerceSettings?.definitiveUrl || '';

        const storeNumber = storeNumbers.find((x) => x.salesChannelID === salesChannel.id)?.storeNumber || 1;

        return [
            <StyledDivElementTable>{T._(`Store ${storeNumber}`, 'Admin')}</StyledDivElementTable>,

            <ZTooltip position={'top'} title={name}>
                <NameCell>{name}</NameCell>
            </ZTooltip>,
            <StyledDivElementTable>
                <StyledLinkContainer>
                    <StyledLink href={storeUrl} target='_blank' title={storeUrl}>
                        <span>{storeUrl}</span>
                        <Icon>
                            <LinkIcon></LinkIcon>
                        </Icon>
                    </StyledLink>
                </StyledLinkContainer>
            </StyledDivElementTable>,
            <StyledDivElementTable>
                <StyledImg>{SalesChannelIcon(salesChannel.ecommerceSettings?.ecommerceName, false)}</StyledImg>
            </StyledDivElementTable>,
            <ActionsColContainer>
                {salesChannel.id !== 'guid://Zakeke/SalesChannel/1' ||
                !['SHOPIFY', 'BIG_COMMERCE', 'WIX'].includes(salesChannel.ecommerceSettings!.ecommerceName) ? (
                    <TextButton onClick={() => handleDeactivateSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE')}>
                        {T._('Disconnect', 'Admin')}
                    </TextButton>
                ) : (
                    <ZTooltip position={'top'} title={T._('To disconnect, remove the app from your store.', 'Admin')}>
                        <UnclickableDisconnectButtonContainer>
                            <UnclickableTextButton>{T._('Disconnect', 'Admin')}</UnclickableTextButton>
                        </UnclickableDisconnectButtonContainer>
                    </ZTooltip>
                )}
                <SelectActions
                    header={
                        <Icon $hoverable>
                            <VerticalThreePoint />
                        </Icon>
                    }
                    items={[
                        <ButtonActions
                            key={'rename_button_' + salesChannel.id}
                            onClick={() => {
                                handleRenameSalesChannel(salesChannel.id);
                            }}
                        >
                            {T._('Rename Sales Channel', 'Admin')}
                        </ButtonActions>,
                        <ButtonActions
                            key={'API_key_button_' + salesChannel.id}
                            onClick={() => {
                                handleIntegrationSettingsAndApiKey(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE');
                            }}
                        >
                            {['API', 'C_CLOUD'].includes(salesChannel.ecommerceSettings!.ecommerceName)
                                ? T._('Integration settings and API Keys', 'Admin')
                                : T._('API Keys', 'Admin')}
                        </ButtonActions>,
                        ,
                        salesChannel.ecommerceSettings?.ecommerceName === 'PRESTA_SHOP' && (
                            <ButtonActions
                                key={'edit_store_url_button_' + salesChannel.id}
                                onClick={() => {
                                    handleChangeStoreUrlClick(salesChannel);
                                }}
                            >
                                {T._('Change store URL', 'Admin')}
                            </ButtonActions>
                        ),
                        salesChannel.ecommerceSettings?.ecommerceName && !['API', 'C_CLOUD', 'NONE'].includes(salesChannel.ecommerceSettings.ecommerceName) && (
                            <ButtonActions
                                key={'integration_settings_button_' + salesChannel.id}
                                onClick={() => {
                                    const platform = salesChannel.ecommerceSettings!.ecommerceName;
                                    const url =
                                        platform === 'SHOPIFY'
                                            ? backofficeUrl(`/settings/integration-settings/SHOPIFY?salesChannelID=${toLocalID(salesChannel.id)}`)
                                            : backofficeUrl(`/settings/integration-settings/${platform}`);
                                    navigate(url);
                                }}
                            >
                                {T._('Go to Integration Settings', 'Admin')}
                            </ButtonActions>
                        ),
                        (canShowSwitchButton ||
                            productSwitcherTasks.some(
                                (task) =>
                                    task.taskType === 'PAYMENT_CONFIRMED' &&
                                    (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing) &&
                                    task.extraData.secondarySalesChannelID?.toString() === toLocalID(salesChannel.id)?.toString()
                            )) &&
                            salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' &&
                            salesChannel.id !== 'guid://Zakeke/SalesChannel/1' && (
                                <ButtonActions
                                    key={'switch_sales_channel' + salesChannel.id}
                                    disabled={checkingSellerPlanForChannelID !== null}
                                    onClick={() => {
                                        handleSwitchSalesChannel(salesChannel.id, salesChannel.ecommerceSettings?.ecommerceName || 'NONE');
                                    }}
                                >
                                    {checkingSellerPlanForChannelID === salesChannel.id ? <Spinner /> : T._('Set as Master Integration', 'Admin')}
                                </ButtonActions>
                            )
                    ]}
                    orientation={'RIGHT'}
                ></SelectActions>
            </ActionsColContainer>
        ];
    };

    const salesChannelIDs = activeSalesChannels.salesChannels.filter((x) => x.ecommerceSettings?.ecommerceName !== 'NONE').map((channel) => channel.id || '');

    const storeNumbers = generateStoreNumbers(salesChannelIDs);

    const masterEcommerceSettings = masterSalesChannel[0]?.ecommerceSettings;

    const canShowAddSalesChannelDialog = searchParams.get('showAddSalesChannelDialog') || 'false';

    const isFirstSalesChannel = !masterEcommerceSettings?.ecommerceGlobalID;

    const showBackButton = !(masterSalesChannel?.length > 0 && otherSalesChannel?.length == 0 && added === 'true');

    useEffect(() => {
        if (added === 'true' && !localStorage.getItem('pendingAction')) {
            showSuccessNotification({
                message: isMaster ? T._('Your Integration has been saved.', 'Admin') : T._('The updated list of sales channels will be shown soon', 'Admin'),
                dismiss: { duration: 5000 }
            });

            const timerId = setTimeout(() => {
                startTransition(() => setFetchKey((prev) => prev + 1));
                navigate(backofficeUrl('/sales-channels'), { replace: true });
            }, 5000);

            return () => clearTimeout(timerId);
        }
    }, [added, isMaster, location.key]);

    useEffect(() => {
        if (canShowAddSalesChannelDialog === 'true') {
            handleAddSaleChannel();
        }
    }, [canShowAddSalesChannelDialog]);

    useEffect(() => {
        // trigger every time the list of channels changes
        const pending = JSON.parse(localStorage.getItem('pendingAction') ?? 'null');
        if (!pending || pending.type !== 'publish-product') return;

        const integrationDone = activeSalesChannels.salesChannels.some((sc) => sc.ecommerceSettings?.ecommerceName !== 'NONE');
        if (!integrationDone) return;

        if (Date.now() - pending.ts > 10 * 60_000) {
            localStorage.removeItem('pendingAction');
            return;
        }

        localStorage.removeItem('pendingAction');

        navigate(backofficeUrl(pending.redirectPage), {
            replace: true,
            state: { doPublishImmediately: true, productID: pending.productId }
        });
    }, [activeSalesChannels.salesChannels]);

    // Handle payment success return from Shopify
    useEffect(() => {
        if (paymentSuccess === 'true' && salesChannelIDParam && chargeId) {
            // First, confirm the Shopify charge
            confirmShopifyCharge({
                variables: {
                    input: {
                        shopifyChargeID: chargeId,
                        chargeType: 'SWITCH_SALES_CHANNELS'
                    }
                },
                onCompleted: () => {
                    // Clear query params after successful confirmation
                    setSearchParams({});

                    // Persist payment state to DB and show dialog
                    persistPaymentAndShowDialog(salesChannelIDParam, masterSalesChannelIDParam || '', true, chargeId, true); // checkbox must have been checked to initiate payment
                },
                onError: () => {
                    // Clear query params even on error
                    setSearchParams({});

                    showErrorNotification({
                        message: T._('Failed to confirm payment. Please contact support.', 'Admin')
                    });
                }
            });
        }
    }, [paymentSuccess, salesChannelIDParam, chargeId]);

    // Restore payment success dialog from DB on page load
    useEffect(() => {
        // Skip if URL params will handle it (payment just completed)
        if (paymentSuccess === 'true') return;

        const pendingPaymentTask = productSwitcherTasks.find(
            (task) => task.taskType === 'PAYMENT_CONFIRMED' && (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing)
        );

        if (!pendingPaymentTask) return;

        const { masterSalesChannelID, secondarySalesChannelID } = pendingPaymentTask.extraData;
        if (!masterSalesChannelID || !secondarySalesChannelID) return;

        // Convert local IDs to global IDs for comparison
        const masterGlobalID = toGlobalID(masterSalesChannelID, 'SalesChannel');
        const secondaryGlobalID = toGlobalID(secondarySalesChannelID, 'SalesChannel');

        // Determine if Shopify by checking ecommerce name of the sales channels
        const isShopify = activeSalesChannels.salesChannels.some(
            (sc) => (sc.id === masterGlobalID || sc.id === secondaryGlobalID) && sc.ecommerceSettings?.ecommerceName === 'SHOPIFY'
        );

        showPaymentSuccessDialogForSwitch(secondarySalesChannelID.toString(), masterSalesChannelID.toString(), isShopify);
    }, [productSwitcherTasks]);

    // Handle return from subscription page (redirected from dashboard)
    useEffect(() => {
        const subscriptionComplete = searchParams.get('subscriptionComplete');

        if (subscriptionComplete === 'true') {
            // Find the last completed EDITSUBSCRIPTION task to get the sales channel IDs
            const completedEditSubscriptionTasks = productSwitcherTasks.filter(
                (task) => task.taskType === 'EDITSUBSCRIPTION' && task.state === TASKSTATUS.Completed
            );

            // Get the task with the highest taskID (most recent)
            const lastEditSubscriptionTask =
                completedEditSubscriptionTasks.length > 0
                    ? completedEditSubscriptionTasks.reduce((max, task) => (task.taskID > max.taskID ? task : max))
                    : null;

            const masterSalesChannelID = lastEditSubscriptionTask?.extraData?.masterSalesChannelID || null;
            const secondarySalesChannelID = lastEditSubscriptionTask?.extraData?.secondarySalesChannelID || null;

            // Only proceed if we have both IDs
            if (masterSalesChannelID && secondarySalesChannelID) {
                // Do not show the dialog if the switch task is already running
                const hasPendingSwitchTask = productSwitcherTasks.some(
                    (task) => task.taskType === 'SWITCHSALESCHANNELS' && (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing)
                );
                if (hasPendingSwitchTask) return;

                // Clear query params
                setSearchParams({});

                // Show final dialog to complete the switch
                showDialog(
                    'complete-sales-channel-switch',
                    <MessageDialog
                        showCloseButton={false}
                        message={
                            <>
                                <h3>{T._('Complete Sales Channel Switch', 'Admin')}</h3>
                                <p>{T._('Thank you for subscribing to your new plan!', 'Admin')}</p>
                                <p>{T._('You can now proceed to switch your products to the new sales channel.', 'Admin')}</p>
                                <p>{T._('Click the button below to start the product migration process.', 'Admin')}</p>
                                <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
                                    {T._('This process will run in the background and you will be notified when it completes.', 'Admin')}
                                </p>
                            </>
                        }
                        buttons={[
                            {
                                label: T._('Switch Products', 'Admin'),
                                onClick: () => {
                                    closeDialog('complete-sales-channel-switch');
                                    handleStartProductSwitch(
                                        toGlobalID(masterSalesChannelID, 'SalesChannel'),
                                        toGlobalID(secondarySalesChannelID, 'SalesChannel')
                                    );
                                }
                            }
                        ]}
                    />
                );
            } else {
                // If we can't find the task, clear params and show error
                setSearchParams({});
                showErrorNotification({
                    message: T._('Unable to complete sales channel switch. Please try again or contact support.', 'Admin')
                });
            }
        }
    }, [searchParams, setSearchParams, showDialog, closeDialog, productSwitcherTasks]);

    // Show "Complete Sales Channel Switch" dialog on page refresh when migration is pending
    useEffect(() => {
        if (!isSellerMigrating) return;
        if (searchParams.get('subscriptionComplete') === 'true') return;
        if (
            productSwitcherTasks.some(
                (task) => task.taskType === 'SWITCHSALESCHANNELS' && (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing)
            )
        )
            return;

        const lastEditSubscriptionTask = productSwitcherTasks
            .filter((task) => task.taskType === 'EDITSUBSCRIPTION' && task.state === TASKSTATUS.Completed)
            .reduce<(typeof productSwitcherTasks)[number] | null>((max, task) => (max === null || task.taskID > max.taskID ? task : max), null);

        const masterSalesChannelID = lastEditSubscriptionTask?.extraData?.masterSalesChannelID || null;
        const secondarySalesChannelID = lastEditSubscriptionTask?.extraData?.secondarySalesChannelID || null;

        if (!masterSalesChannelID || !secondarySalesChannelID) return;

        showDialog(
            'complete-sales-channel-switch',
            <MessageDialog
                showCloseButton={false}
                message={
                    <>
                        <h3>{T._('Complete Sales Channel Switch', 'Admin')}</h3>
                        <p>{T._('Thank you for subscribing to your new plan!', 'Admin')}</p>
                        <p>{T._('You can now proceed to switch your products to the new sales channel.', 'Admin')}</p>
                        <p>{T._('Click the button below to start the product migration process.', 'Admin')}</p>
                        <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
                            {T._('This process will run in the background and you will be notified when it completes.', 'Admin')}
                        </p>
                    </>
                }
                buttons={[
                    {
                        label: T._('Switch Products', 'Admin'),
                        onClick: () => {
                            closeDialog('complete-sales-channel-switch');
                            handleStartProductSwitch(
                                toGlobalID(masterSalesChannelID.toString(), 'SalesChannel'),
                                toGlobalID(secondarySalesChannelID.toString(), 'SalesChannel')
                            );
                        }
                    }
                ]}
            />
        );
    }, [isSellerMigrating, searchParams, productSwitcherTasks, showDialog, closeDialog]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
            }
        };
    }, []);

    // Restore unsubscribing-plan dialog after a page refresh
    useEffect(() => {
        const stored = localStorage.getItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
        if (!stored) return;

        const { taskID, salesChannelID, masterSalesChannelID, flowType } = JSON.parse(stored) as {
            taskID: string;
            salesChannelID: string;
            masterSalesChannelID: string;
            flowType?: 'shopify' | 'nonShopify';
        };

        showDialog(
            'unsubscribing-plan',
            <MessageDialog
                showCloseButton={true}
                message={
                    <>
                        <Center>
                            <Spinner />
                        </Center>
                        <p style={{ marginTop: '20px', textAlign: 'center' }}>{T._('Processing your subscription cancellation...', 'Admin')}</p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('Your current plan will be cancelled and you will be redirected to subscribe to a new plan.', 'Admin')}
                        </p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('You can close this dialog. The process will continue in the background.', 'Admin')}
                        </p>
                    </>
                }
                buttons={[]}
            />
        );

        const resumeCallback = flowType === 'nonShopify' ? () => handleStartProductSwitch(masterSalesChannelID, salesChannelID) : undefined;
        startPollingTask(taskID, salesChannelID, masterSalesChannelID, resumeCallback, flowType);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUnsubscribeCurrentPlan = (salesChannelID: string, masterSalesChannelID: string) => {
        // Show dialog with loader
        showDialog(
            'unsubscribing-plan',
            <MessageDialog
                showCloseButton={true}
                message={
                    <>
                        <Center>
                            <Spinner />
                        </Center>
                        <p style={{ marginTop: '20px', textAlign: 'center' }}>{T._('Processing your subscription cancellation...', 'Admin')}</p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('Your current plan will be cancelled and you will be redirected to subscribe to a new plan.', 'Admin')}
                        </p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('You can close this dialog. The process will continue in the background.', 'Admin')}
                        </p>
                    </>
                }
                buttons={[]}
            />
        );

        // Start the unsubscribe task
        generateUnsubscribeTask({
            variables: {
                input: {
                    masterSalesChannelID: masterSalesChannelID,
                    secondarySalesChannelID: salesChannelID,
                    environment: 'CUSTOMIZER'
                }
            },
            onCompleted: (response) => {
                // Start polling for task completion
                if (!response.generateEditSubscriptionTask) {
                    closeDialog('unsubscribing-plan');
                    showErrorNotification({
                        message: T._('Failed to start subscription cancellation. Please try again.', 'Admin')
                    });
                    return;
                }
                const taskID = response.generateEditSubscriptionTask.taskID;
                startPollingTask(taskID.toString(), salesChannelID, masterSalesChannelID);
            },
            onError: () => {
                closeDialog('unsubscribing-plan');
                showErrorNotification({
                    message: T._('Failed to start subscription cancellation. Please try again.', 'Admin')
                });
            }
        });
    };

    const handleEditSubscriptionForNonShopify = (salesChannelID: string, masterSalesChannelID: string) => {
        showDialog(
            'unsubscribing-plan',
            <MessageDialog
                showCloseButton={true}
                message={
                    <>
                        <Center>
                            <Spinner />
                        </Center>
                        <p style={{ marginTop: '20px', textAlign: 'center' }}>{T._('Generating sales channel switch task...', 'Admin')}</p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('The sales channel switch task is being generated. Products will be migrated once complete.', 'Admin')}
                        </p>
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#666' }}>
                            {T._('You can close this dialog. The process will continue in the background.', 'Admin')}
                        </p>
                    </>
                }
                buttons={[]}
            />
        );

        generateUnsubscribeTask({
            variables: {
                input: {
                    masterSalesChannelID: masterSalesChannelID,
                    secondarySalesChannelID: salesChannelID,
                    environment: 'CUSTOMIZER'
                }
            },
            onCompleted: (response) => {
                if (!response.generateEditSubscriptionTask) {
                    closeDialog('unsubscribing-plan');
                    showErrorNotification({
                        message: T._('Failed to generate sales channel switch task. Please try again.', 'Admin')
                    });
                    return;
                }
                const taskID = response.generateEditSubscriptionTask.taskID;
                startPollingTask(
                    taskID.toString(),
                    salesChannelID,
                    masterSalesChannelID,
                    () => handleStartProductSwitch(masterSalesChannelID, salesChannelID),
                    'nonShopify'
                );
            },
            onError: () => {
                closeDialog('unsubscribing-plan');
                showErrorNotification({
                    message: T._('Failed to generate sales channel switch task. Please try again.', 'Admin')
                });
            }
        });
    };

    const startPollingTask = (
        taskID: string,
        salesChannelID: string,
        masterSalesChannelID: string,
        onCompleted?: () => void,
        flowType?: 'shopify' | 'nonShopify'
    ) => {
        // Convert taskID to global ID format for the query
        const globalTaskID = toGlobalID(taskID, 'ProductSwitcherTask');

        // Persist task info so the dialog can be restored after a page refresh
        localStorage.setItem(UNSUBSCRIBE_TASK_STORAGE_KEY, JSON.stringify({ taskID, salesChannelID, masterSalesChannelID, flowType }));

        // Poll every 3 seconds
        pollingIntervalRef.current = setInterval(() => {
            fetchQuery<salesChannelPollUnsubscribeTaskQuery>(Environment, pollUnsubscribeTaskQuery, { id: globalTaskID })
                .toPromise()
                .then((data) => {
                    if (!data?.productSwitcherTask) return;

                    const task = data.productSwitcherTask;

                    // Check if task is completed (state === 2)
                    if (task.state === TASKSTATUS.Completed) {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                        }
                        if (pollingTimeoutRef.current) {
                            clearTimeout(pollingTimeoutRef.current);
                        }
                        localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
                        closeDialog('unsubscribing-plan');
                        if (onCompleted) {
                            onCompleted();
                        } else {
                            window.location.reload();
                        }
                    } else if (task.state === TASKSTATUS.Error) {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                        }
                        if (pollingTimeoutRef.current) {
                            clearTimeout(pollingTimeoutRef.current);
                        }
                        localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
                        closeDialog('unsubscribing-plan');

                        // Show error dialog suggesting to contact support
                        showDialog(
                            'unsubscribe-task-error',
                            <MessageDialog
                                showCloseButton={true}
                                message={
                                    <>
                                        <h3 style={{ color: '#d32f2f', marginBottom: '16px' }}>{T._('Subscription Cancellation Failed', 'Admin')}</h3>
                                        <p>{T._('An error occurred while processing your subscription cancellation.', 'Admin')}</p>
                                        {task.lastError && (
                                            <p style={{ marginTop: '8px', fontSize: '13px', color: '#d32f2f' }}>
                                                {T._('Error details:', 'Admin')} {task.lastError}
                                            </p>
                                        )}
                                        <p style={{ marginTop: '16px' }}>{T._('Please contact the Zakeke support team for assistance:', 'Admin')}</p>
                                        <p style={{ marginTop: '8px' }}>
                                            {T._('Email:', 'Admin')} <a href='mailto:support@zakeke.com'>support@zakeke.com</a>
                                        </p>
                                    </>
                                }
                                buttons={[
                                    {
                                        label: T._('Close', 'Admin'),
                                        onClick: () => closeDialog('unsubscribe-task-error')
                                    }
                                ]}
                            />
                        );
                    }
                    // Otherwise continue polling (states 0=Waiting or 1=Processing)
                })
                .catch(() => {
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                    }
                    if (pollingTimeoutRef.current) {
                        clearTimeout(pollingTimeoutRef.current);
                    }
                    localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
                    closeDialog('unsubscribing-plan');

                    showErrorNotification({
                        message: T._('Failed to check task status. Please refresh the page.', 'Admin')
                    });
                });
        }, 5000); // Poll every 5 seconds

        // Stop polling after 2 hours and recommend contacting support
        pollingTimeoutRef.current = setTimeout(() => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
            closeDialog('unsubscribing-plan');
            showDialog(
                'unsubscribe-task-timeout',
                <MessageDialog
                    showCloseButton={true}
                    message={
                        <>
                            <h3 style={{ marginBottom: '16px' }}>{T._('Subscription Cancellation Taking Too Long', 'Admin')}</h3>
                            <p>{T._('The subscription cancellation process has been running for more than 2 hours without completing.', 'Admin')}</p>
                            <p style={{ marginTop: '16px' }}>{T._('Please contact the Zakeke support team for assistance:', 'Admin')}</p>
                            <p style={{ marginTop: '8px' }}>
                                {T._('Email:', 'Admin')} <a href='mailto:support@zakeke.com'>support@zakeke.com</a>
                            </p>
                        </>
                    }
                    buttons={[
                        {
                            label: T._('Close', 'Admin'),
                            onClick: () => closeDialog('unsubscribe-task-timeout')
                        }
                    ]}
                />
            );
        }, 7200000); // 2 hours timeout
    };

    const handleStartProductSwitch = (masterSalesChannelID: string, secondarySalesChannelID: string) => {
        // Use generateProductSwitcherTask mutation
        generateProductSwitcherTask({
            variables: {
                input: {
                    masterSalesChannelID: masterSalesChannelID,
                    secondarySalesChannelID: secondarySalesChannelID
                }
            },
            onCompleted: (response) => {
                const taskID = response.generateProductSwitcherTask?.taskID;
                // Full page reload to task monitoring page to clear any open dialogs
                window.location.href = backofficeUrl('/sales-channels/switch-tasks') + (taskID != null ? `?newTaskId=${taskID}` : '');
            },
            onError: () => {
                showErrorNotification({
                    message: T._('Failed to start product switch. Please try again.', 'Admin')
                });
            }
        });
    };

    const showPaymentSuccessDialogForSwitch = (salesChannelID: string, masterSalesChannelID: string, isShopify: boolean) => {
        showDialog(
            'payment-success-dialog',
            <PaymentSuccessDialog
                salesChannelID={salesChannelID}
                isShopify={isShopify}
                onProceedToNextStep={() => {
                    closeDialog('payment-success-dialog');
                    if (isShopify) {
                        // Shopify: unsubscribe → resubscribe → product switch
                        handleUnsubscribeCurrentPlan(toGlobalID(salesChannelID, 'SalesChannel'), toGlobalID(masterSalesChannelID, 'SalesChannel'));
                    } else {
                        // Non-Shopify: generate EditSubscriptionTask first, then ProductSwitcherTask on completion
                        handleEditSubscriptionForNonShopify(toGlobalID(salesChannelID, 'SalesChannel'), toGlobalID(masterSalesChannelID, 'SalesChannel'));
                    }
                }}
                onCancel={() => {
                    closeDialog('payment-success-dialog');
                }}
            />
        );
    };

    const persistPaymentAndShowDialog = (
        salesChannelID: string,
        masterSalesChannelID: string,
        isShopify: boolean,
        shopifyChargeID: string | null,
        hasAcknowledgedInfo: boolean
    ) => {
        createPaymentConfirmedTask({
            variables: {
                input: {
                    masterSalesChannelID: toGlobalID(masterSalesChannelID, 'SalesChannel'),
                    secondarySalesChannelID: toGlobalID(salesChannelID, 'SalesChannel'),
                    shopifyChargeID: shopifyChargeID,
                    hasAcknowledgedInformation: hasAcknowledgedInfo
                }
            },
            onCompleted: () => {
                refetch({}, { fetchPolicy: 'network-only' });
            }
        });

        showPaymentSuccessDialogForSwitch(salesChannelID, masterSalesChannelID, isShopify);
    };

    const handleAddSaleChannel = () => {
        if (isFirstSalesChannel) {
            navigate(backofficeUrl('/connect-shop'));
            return;
        }

        query.shopifyAuthEcommerceSettings?.isTestStore && config.isProduction
            ? showDialog(
                  'add-sales-channel-not-available',
                  <MessageDialog
                      windowDecorator={SalesChannelNotAvailableDialogWindow}
                      message={
                          <>
                              {T._(
                                  "We're sorry, but you cannot add sales channels at this time. Your account is currently in trial mode on Shopify or is a development store.",
                                  'Admin'
                              )}
                              <br />
                              <br />
                              {T._('To access this feature, please upgrade to an active Shopify plan.', 'Admin')}
                              <br />
                              <br />
                              {T._('Thank you for your understanding.', 'Admin')}
                          </>
                      }
                      buttons={[
                          {
                              label: T._('OK', 'Admin'),
                              onClick: () => {
                                  closeDialog('add-sales-channel-not-available');
                              }
                          }
                      ]}
                  />
              )
            : showDialog(
                  'add-sales-channel',
                  <AddSalesChannelDialog
                      onSalesChannelCreated={() => {
                          closeDialog('add-sales-channel');
                          setFetchKey((prev) => prev + 1);
                      }}
                  ></AddSalesChannelDialog>
              );
    };

    const handleDeactivateSalesChannel = (id: string, ecommerce: ECommerce) => {
        if (ecommerce === 'SHOPIFY' || ecommerce === 'BIG_COMMERCE') {
            showDialog(
                'disconnect-channel',
                <MessageDialog
                    message={T._('You must remove the Zakeke app from your store to disconnect this sales channel', 'Admin')}
                    onClose={() => {
                        closeDialog('disconnect-channel');
                    }}
                />
            );
        } else {
            showDialog(
                'disconnect-channel',
                <QuestionDialog
                    message={
                        id !== 'guid://Zakeke/SalesChannel/1' ? (
                            T._(`Do you want disconnect the sales channel?`, 'Admin')
                        ) : (
                            <>
                                {T._(
                                    'Some of the products in your Customizable Products list originate from this sales channel. If you disconnect it, they will no longer be available for customization by your customers even on other sales channels.',
                                    'Admin'
                                )}
                                <br />
                                <br />
                                {T._('Affected products:', 'Admin')}
                                <Suspense fallback={<Spinner />}>
                                    <DefaultSalesChannelProductsList />
                                </Suspense>
                            </>
                        )
                    }
                    buttonYesLabel={toLocalID(id) == 1 ? T._('Cancel and contact us', 'Admin') : undefined}
                    buttonNoLabel={toLocalID(id) == 1 ? T._('Disconnect Anyway', 'Admin') : undefined}
                    destructiveNo={toLocalID(id) == 1}
                    onYesClick={() => {
                        closeDialog('disconnect-channel');

                        if (toLocalID(id) == 1) {
                            window.open('https://zakeke.zendesk.com/hc/en-us/requests/new', '_blank');
                            return;
                        }

                        const loadingNotification = showNotification({
                            type: 'default',
                            message: T._(`Disconnecting sales channel...`, 'Admin')
                        });

                        // TODO: Scroll on top
                        setSalesChannelStatus({
                            variables: {
                                id: id,
                                status: 'DISABLED'
                            },
                            onError: () => {
                                removeNotification(loadingNotification);
                                showErrorNotification({ message: T._('Failed disconnect sales channel', 'Admin') });
                            },
                            onCompleted: () => {
                                removeNotification(loadingNotification);
                                showSuccessNotification({ message: T._('Sales channel disconnected.', 'Admin') });
                                refetch({}, { fetchPolicy: 'network-only' });
                            }
                        });
                    }}
                    onNoClick={() => {
                        if (toLocalID(id) != 1) {
                            closeDialog('disconnect-channel');
                            return;
                        }

                        closeDialog('disconnect-channel');

                        const loadingNotification = showNotification({
                            type: 'default',
                            message: T._(`Disconnecting sales channel...`, 'Admin')
                        });

                        removeMasterIntegration({
                            variables: {
                                salesChannelID: null // Master integration
                            },
                            onError: () => {
                                removeNotification(loadingNotification);
                                showErrorNotification({ message: T._('Failed disconnect sales channel', 'Admin') });
                            },
                            onCompleted: () => {
                                removeNotification(loadingNotification);
                                showSuccessNotification({ message: T._('Sales channel disconnected.', 'Admin') });
                                setFetchKey((prev) => prev + 1);
                                if (added === 'true') {
                                    navigate(backofficeUrl('/sales-channels'));
                                }
                            }
                        });
                    }}
                />
            );
        }
    };

    const handleRenameSalesChannel = (id: string) => {
        showDialog(
            'rename-sales-channel-dialog',
            <RenameSalesChannelDialog
                id={id}
                onClosed={() => {
                    closeDialog('rename-sales-channel-dialog');
                    refetch({}, { fetchPolicy: 'network-only' });
                }}
            />
        );
    };

    const handleIntegrationSettingsAndApiKey = (id: string, ecommerce: string) => {
        if (ecommerce === 'API') {
            showDialog('api-connection', <ApiIntegrationSalesChannelDialog salesChannelID={id || ''} hasMasterIntegration={true} />);
        } else if (ecommerce === 'C_CLOUD') {
            showDialog('sales-force', <CCloudIntegrationSalesChannelDialog salesChannelID={id || ''} />);
        } else {
            showDialog('api-key', <ApiKeyDialog salesChannelID={id} />);
        }
    };

    const handleChangeStoreUrlClick = (salesChannel: salesChannelsActive$data['salesChannels'][0]) => {
        showDialog('store-url-sales-channel', <StoreUrlSalesChannelDialog salesChannel={salesChannel} />);
    };

    const openSwitchSalesChannelDialog = (
        salesChannelID: string,
        secondaryEcommerceName: string,
        planCheck?: SellerPlanCheckInfo,
        configuratorModelsCount?: number,
        hasPodProducts?: boolean
    ) => {
        showDialog(
            'switch-sales-channel-dialog',
            <SwitchSalesChannelDialog
                secondarySalesChannelID={salesChannelID}
                secondaryEcommerceName={secondaryEcommerceName}
                masterSalesChannelID={masterEcommerceSettings?.id || ''}
                masterEcommerceName={masterEcommerceSettings?.ecommerceName || 'NONE'}
                planCheck={planCheck}
                configuratorModelsCount={configuratorModelsCount}
                hasPodProducts={hasPodProducts}
                onClosed={() => {
                    closeDialog('switch-sales-channel-dialog');
                    refetch({}, { fetchPolicy: 'network-only' });
                }}
                onPaymentSuccess={(scID, masterScID, isShopify, chargeID, hasAcknowledgedInfo) => {
                    persistPaymentAndShowDialog(scID, masterScID, isShopify, chargeID, hasAcknowledgedInfo);
                }}
            />
        );
    };

    const handleSwitchSalesChannel = (salesChannelID: string, secondaryEcommerceName: string) => {
        // If the secondary store is not Shopify/API and the master integration is API, the switch
        // requires manual intervention — prompt the user to contact support.
        if (secondaryEcommerceName !== 'SHOPIFY' && secondaryEcommerceName !== 'API' && masterEcommerceSettings?.ecommerceName === 'API') {
            showDialog(
                'switch-not-supported',
                <MessageDialog
                    message={
                        <>
                            <h3>{T._('Switch not supported', 'Admin')}</h3>
                            <p>
                                {T._(
                                    'Switching this store to Master Integration is not supported when the current Master Integration is API. Please contact Zakeke support for assistance.',
                                    'Admin'
                                )}
                            </p>
                        </>
                    }
                    buttons={[{ label: T._('OK', 'Admin'), onClick: () => closeDialog('switch-not-supported') }]}
                />
            );
            return;
        }

        // If a PAYMENT_CONFIRMED task is already waiting for this channel, the user paid
        // but closed the success dialog — reopen it instead of restarting the payment flow.
        const localID = toLocalID(salesChannelID)?.toString();
        const pendingPaymentTask = productSwitcherTasks.find(
            (task) =>
                task.taskType === 'PAYMENT_CONFIRMED' &&
                (task.state === TASKSTATUS.Waiting || task.state === TASKSTATUS.Processing) &&
                task.extraData.secondarySalesChannelID?.toString() === localID
        );

        if (pendingPaymentTask) {
            const { masterSalesChannelID, secondarySalesChannelID } = pendingPaymentTask.extraData;
            if (masterSalesChannelID && secondarySalesChannelID) {
                const masterGlobalID = toGlobalID(masterSalesChannelID.toString(), 'SalesChannel');
                const secondaryGlobalID = toGlobalID(secondarySalesChannelID.toString(), 'SalesChannel');
                const isShopify = activeSalesChannels.salesChannels.some(
                    (sc) => (sc.id === masterGlobalID || sc.id === secondaryGlobalID) && sc.ecommerceSettings?.ecommerceName === 'SHOPIFY'
                );
                showPaymentSuccessDialogForSwitch(secondarySalesChannelID.toString(), masterSalesChannelID.toString(), isShopify);
                return;
            }
        }

        setCheckingSellerPlanForChannelID(salesChannelID);
        fetchQuery<salesChannelCheckSellerPlanQuery>(Environment, checkSellerPlanQuery, {})
            .toPromise()
            .then((data) => {
                setCheckingSellerPlanForChannelID(null);
                openSwitchSalesChannelDialog(
                    salesChannelID,
                    secondaryEcommerceName,
                    data?.checkSellerPlan ?? undefined,
                    data?.modelsCount ?? undefined,
                    data?.hasPodProducts ?? undefined
                );
            })
            .catch(() => {
                setCheckingSellerPlanForChannelID(null);
            });
    };

    const handleViewSwitchTasks = () => {
        navigate(backofficeUrl('/sales-channels/switch-tasks'));
    };

    const hasMasterEcommerce = !!(masterEcommerceSettings?.ecommerceName && masterEcommerceSettings.ecommerceName !== 'NONE');

    return (
        <StyledContainerBase showBackButton={showBackButton}>
            {hasMasterEcommerce && (
                <PageHeader
                    title={T._('Sales channels', 'Admin')}
                    backLabel={T._('Back', 'Admin')}
                    description={<>{T._('Connect multiple stores to Zakeke and manage them all from a single place.', 'Admin')}</>}
                    learnMoreLabel={T._('Learn more', 'Admin')}
                    learnMoreUrl='https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels'
                    isBackVisible={showBackButton}
                    actions={
                        <ActionsGroup>
                            {masterEcommerceSettings!.ecommerceName !== 'AMAZON' && (
                                <>
                                    <Icon
                                        $hoverable
                                        tooltip={T._('Integration Settings', 'Admin')}
                                        onClick={() => navigate(backofficeUrl('/settings/integration-settings'))}
                                    >
                                        <CogIcon />
                                    </Icon>
                                    {(otherSalesChannel.length > 0 || productSwitcherTasks.length > 0) && <ActionsDivider />}
                                </>
                            )}
                            {otherSalesChannel.length > 0 && (
                                <PrimaryButton disabled={state.lastUsedTool === 2} $size='medium' onClick={() => handleAddSaleChannel()}>
                                    <MaterialIcon name='add' />
                                    {T._('Add', 'Admin')}
                                </PrimaryButton>
                            )}
                            {productSwitcherTasks.length > 0 && (
                                <BasicButton onClick={() => handleViewSwitchTasks()}>{T._('Sales Channels Tasks', 'Admin')}</BasicButton>
                            )}
                        </ActionsGroup>
                    }
                />
            )}

            {hasMasterEcommerce ? (
                <>
                    {added !== 'false' && isPending && Loader}
                    {!isPending && (
                        <React.Fragment>
                            {masterSalesChannel && (
                                <StyledCard>
                                    <StyledCardTitle>
                                        {T._('Master Integration', 'Admin')}
                                        <HelpIconContainer
                                            tooltip={T._(
                                                'The Master Integration is the first store you connect to Zakeke. It serves as the primary integration that controls all other sales channels. If you remove the Master Integration, all connected channels will also be disabled. Additionally, all transaction fees for other sales channels are billed to the Master Integration.',
                                                'Admin'
                                            )}
                                        >
                                            <HelpIcon />
                                        </HelpIconContainer>
                                    </StyledCardTitle>
                                    <Table headings={headings} compact>
                                        {masterSalesChannel.map((salesChannel, i) => {
                                            return <Row key={i.toString()} cells={buildRowCells(salesChannel)} />;
                                        })}
                                    </Table>
                                </StyledCard>
                            )}

                            {otherSalesChannel.length > 0 ? (
                                state.lastUsedTool === 2 ? (
                                    <ZTooltip position={'top'} title={T._('Sales Channels are not supported for 3D Configurator', 'Admin')}>
                                        <DisabledSalesChannelContainer>
                                            <StyledCard>
                                                <StyledCardTitle>{T._('Sales Channels', 'Admin')}</StyledCardTitle>
                                                <Table headings={headings} compact>
                                                    {otherSalesChannel.map((salesChannel, i) => {
                                                        return <Row key={i.toString()} cells={buildRowCells(salesChannel)} />;
                                                    })}
                                                </Table>
                                            </StyledCard>
                                        </DisabledSalesChannelContainer>
                                    </ZTooltip>
                                ) : (
                                    <StyledCard>
                                        <StyledCardTitle>{T._('Sales Channels', 'Admin')}</StyledCardTitle>
                                        <Table headings={headings} compact>
                                            {otherSalesChannel.map((salesChannel, i) => {
                                                return <Row key={i.toString()} cells={buildRowCells(salesChannel)} />;
                                            })}
                                        </Table>
                                    </StyledCard>
                                )
                            ) : (
                                <EmptyList
                                    hideIcon={true}
                                    title={T._('Sales Channels', 'Admin')}
                                    subTitle={
                                        <>
                                            {T._('Connect your other stores to this account and manage everything from a single place. ', 'Admin')}
                                            <Link href='https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels' target='_blank'>
                                                {T._('Learn more', 'Admin')}
                                            </Link>
                                        </>
                                    }
                                    alreadyExistingButton={
                                        <PrimaryButton onClick={handleAddSaleChannel}>
                                            <MaterialIcon name='add' />
                                            {T._('Add Sales Channels', 'Admin')}
                                        </PrimaryButton>
                                    }
                                />
                            )}
                        </React.Fragment>
                    )}
                </>
            ) : (
                <>
                    {added !== 'false' && Loader}
                    {added === 'false' && (
                        <EmptyList
                            title={T._('Connect Zakeke to your E-Commerce channels', 'Admin')}
                            subTitle={T._(
                                'Ready to connect and manage multiple commerce channels to your Zakeke account? \nStart by connecting Zakeke with one of your stores first.',
                                'Admin'
                            )}
                            learnMoreText={T._('Learn more', 'Admin')}
                            learnMoreLink='https://zakeke.zendesk.com/hc/en-us/sections/360003697399-Store-integration-and-setup'
                            alreadyExistingButton={<PrimaryButton onClick={handleAddSaleChannel}>{T._('Connect your store now', 'Admin')}</PrimaryButton>}
                        />
                    )}
                </>
            )}
        </StyledContainerBase>
    );
};

export default SalesChannels;
